import * as z from 'zod';
import {translations} from 'presentation/translations-new';
import {validateCNPJ, validateCPF} from 'presentation/utils';
import {PersonTypeEnum, SearchDoctorByEnum} from '../../components/ProviderData/constants/types';

const {
	errors: {invalidCPF, invalidCNPJ},
	invalidSelectProfessional,
} = translations['pt-br'].privateFlow.refund.receiptRegistrationPage.providerData;
const {requiredField} = translations['pt-br'].commons;

export const providerDataSchema = (allowAlphanumericDocument = true) =>
	z
		.object({
			searchDoctorBy: z
				.enum([SearchDoctorByEnum.DOCUMENT, SearchDoctorByEnum.COUNCIL_NUMBER])
				.default(SearchDoctorByEnum.DOCUMENT),
			doctorNotFound: z.boolean().default(false),
			professionalNotFound: z.boolean().default(false).optional(),
			providerUnauthorized: z.boolean().default(false),
			providerDocumentMatchUserCpf: z.boolean().default(false),
			providerName: z.string().max(50).default(''),
			professionalName: z.string().min(1),
			providerCode: z.string().default(''),
			professionalCode: z.string().default(''),
			professionalType: z
				.enum([PersonTypeEnum.PHYSICAL_PERSON, PersonTypeEnum.LEGAL_PERSON])
				.default(PersonTypeEnum.PHYSICAL_PERSON),
			councilAcronym: z.object({
				acronym: z.string().min(1),
				description: z.string(),
			}),
			councilNumber: z
				.string()
				.trim()
				.transform(v => v.replace(/\D/g, ''))
				.pipe(z.string().min(1, {message: requiredField})),
			councilState: z.object({
				code: z.string().min(1),
				state: z.string(),
			}),
			professionalDocument: z
				.string({required_error: requiredField, invalid_type_error: requiredField})
				.trim()
				.transform(v =>
					allowAlphanumericDocument
						? v.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
						: v.replace(/\D/g, ''),
				),
			professionalCpf: z
				.string({required_error: requiredField, invalid_type_error: requiredField})
				.trim()
				.transform(v => v.replace(/\D/g, '')),
		})
		.superRefine((data, ctx) => {
			const isLegalPerson = data.professionalType === PersonTypeEnum.LEGAL_PERSON;
			const isCouncilNumber = data.searchDoctorBy === SearchDoctorByEnum.COUNCIL_NUMBER;
			const isLegalPersonAndCouncilNumber = isLegalPerson && isCouncilNumber;

			const validations = [
				{
					condition: data.professionalCpf.length === 0 && !isLegalPersonAndCouncilNumber,
					path: 'professionalCpf',
					message: requiredField,
				},
				{
					condition: !validateCPF(data.professionalCpf) && !isLegalPersonAndCouncilNumber,
					path: 'professionalCpf',
					message: invalidCPF,
				},
				{
					condition: isLegalPerson && data.professionalDocument.length === 0,
					path: 'professionalDocument',
					message: requiredField,
				},
				{
					condition:
						isLegalPerson &&
						!validateCNPJ(data.professionalDocument, allowAlphanumericDocument),
					path: 'professionalDocument',
					message: invalidCNPJ,
				},
				{
					condition: isLegalPerson && data.providerName.length === 0,
					path: 'providerName',
					message: requiredField,
				},
				{
					condition:
						!isLegalPerson &&
						!isCouncilNumber &&
						!data.doctorNotFound &&
						validateCPF(data.professionalCpf) &&
						data.professionalName.length === 0,
					path: 'professionalCpf',
					message: invalidSelectProfessional,
				},
			];

			validations.forEach(({condition, path, message}) => {
				if (condition) {
					ctx.addIssue({
						path: [path],
						code: z.ZodIssueCode.custom,
						message,
					});
				}
			});
		})
		.refine(data => {
			if (data.professionalType === PersonTypeEnum.LEGAL_PERSON) {
				return (
					data.providerName &&
					data.professionalName &&
					data.councilAcronym &&
					data.councilNumber &&
					data.councilState &&
					data.professionalDocument
				);
			}

			return (
				data.professionalName &&
				data.councilAcronym &&
				data.councilNumber &&
				data.councilState &&
				data.professionalCpf
			);
		});

export type ProviderDataSchema = z.infer<ReturnType<typeof providerDataSchema>>;
